/**
 * Pit to Port: a cargo delivery's inland loading and delivery locations
 * (Chris's walk-through, go-live 1 November 2026). The rules the screen
 * needs, in one place for the classic and new-look HOW tabs. The server
 * (deliveries/services/route_locations.py) checks the same rules on save and
 * has the last word.
 *
 * A stop on formData.route_locations:
 *   { id?, kind: 'loading'|'delivery', crm_id, partner_name, partner_is_draft,
 *     address_id, address, typing, new_address, new_address_data, quantity }
 * address_id picks one of the partner's saved addresses; new_address is typed
 * here (typing: the person chose "Type a new address") and saved onto the
 * partner with the delivery.
 */
import { addFigures, subtractFigures, QUANTITY_DECIMALS } from '../../utils/precision';
import { findUnit, unitLabel } from '../../utils/unitLabels';

export const KINDS = ['loading', 'delivery'];

export const KIND_WORDS = {
  loading: { one: 'Loading location', many: 'Loading locations', add: 'Add loading location', total: 'bought total' },
  delivery: { one: 'Delivery location', many: 'Delivery locations', add: 'Add delivery location', total: 'sold total' },
};

// Which delivery total each side adds up to.
const SIDE_TOTAL = {
  loading: ['total_purchase_product_quantity', 'total_purchase_product_unit'],
  delivery: ['total_sales_product_quantity', 'total_sales_product_unit'],
};

// The partner type a draft partner made from each side starts with.
export const DRAFT_PARTNER_TYPE = { loading: 'Producer', delivery: 'End User' };

// The dropdown choice that switches a stop to typing its address.
export const TYPE_NEW = '__type_new__';

// A row's React key. Saved stops use their id; a new stop gets a key once,
// in blankStop, and keeps it. Reading must never mint a new key: a key that
// changed on every redraw lost the first edit to the row (screen check
// 2026-10-20). A stop with neither falls back to its place on its side.
let nextKey = 1;
const keyed = (stop, fallback) => (stop.key ? stop : { ...stop, key: stop.id ? `s${stop.id}` : fallback });

export const allStops = (formData) => (Array.isArray(formData?.route_locations) ? formData.route_locations : []);

export const stopsOf = (formData, kind) => allStops(formData)
  .filter((s) => s.kind === kind)
  .map((s, index) => keyed(s, `i-${kind}-${index}`));

/** The whole list with one side replaced, loading first. */
export const withSide = (formData, kind, stops) => {
  const other = stopsOf(formData, kind === 'loading' ? 'delivery' : 'loading');
  return kind === 'loading' ? [...stops, ...other] : [...other, ...stops];
};

export const blankStop = (kind) => keyed({
  kind,
  crm_id: null,
  partner_name: '',
  partner_is_draft: false,
  address_id: null,
  address: '',
  typing: false,
  new_address: '',
  new_address_data: null,
  quantity: '',
}, `n${nextKey++}`);

/** A stop after its partner is picked (or cleared): its address starts over. */
export const withPartner = (stop, crmId, name, isDraft = false) => ({
  ...stop,
  crm_id: crmId || null,
  partner_name: name || '',
  partner_is_draft: Boolean(crmId) && isDraft,
  address_id: null,
  address: '',
  typing: false,
  new_address: '',
  new_address_data: null,
});

/** A stop after a dropdown choice: a saved address, or typing a new one. */
export const withAddressChoice = (stop, choice, sites) => {
  if (choice === TYPE_NEW) {
    return { ...stop, address_id: null, address: '', typing: true, new_address: stop.new_address || '' };
  }
  const site = (sites || []).find((s) => String(s.id) === String(choice));
  if (!site) return { ...stop, address_id: null, address: '', typing: false };
  return { ...stop, address_id: site.id, address: site.address, typing: false, new_address: '', new_address_data: null };
};

/** True when the stop is typing its address rather than picking a saved one. */
export const isTyping = (stop, sites, sitesLoaded) => {
  if (stop.address_id) return false;
  if (stop.typing) return true;
  if (stop.new_address !== '' && stop.new_address !== undefined && stop.new_address !== null) return true;
  // A partner with no saved address goes straight to typing.
  return Boolean(stop.crm_id) && sitesLoaded && (sites || []).length === 0 && !stop.address;
};

// The tonnes box is the shared number box (ui/AmountInput), which hands back
// a plain dot-decimal figure and refuses a 7th decimal as it is typed, so the
// tally reads exactly what the server will.
const readFigure = (value) => {
  const text = String(value ?? '').trim();
  return /^\d+(\.\d+)?$/.test(text) ? text : null;
};

/** The unit a side's tonnes are counted in: the unit of the total they add up to. */
export const sideUnit = (formData, kind, units) => {
  const raw = formData?.[SIDE_TOTAL[kind][1]];
  if (raw === null || raw === undefined || raw === '') return null;
  return findUnit(units, raw)
    || (units || []).find((u) => (u.code || '').toLowerCase() === String(raw).toLowerCase())
    || null;
};

export const sideUnitWords = (formData, kind, units) => {
  const unit = sideUnit(formData, kind, units);
  return unit ? unitLabel(unit) : String(formData?.[SIDE_TOTAL[kind][1]] || '');
};

/**
 * How a side's tonnes stand against its total, for the line under the stops:
 *   { state: 'none' }                       no tonnes typed
 *   { state: 'missing', label }             two or more stops, one has none
 *   { state: 'no-total', added }            nothing to add up to yet
 *   { state: 'ok', added, total }           adds up
 *   { state: 'off', added, total, left }    does not (left < 0 is too much)
 * Figures are formatted for display.
 */
export const sideTally = (formData, kind) => {
  const stops = stopsOf(formData, kind);
  const figures = stops.map((s) => readFigure(s.quantity));
  const typed = figures.filter((f) => f !== null);
  if (stops.length >= 2) {
    const gap = figures.findIndex((f) => f === null);
    if (gap >= 0) {
      return { state: 'missing', label: stopLabel(kind, gap, stops[gap].partner_name) };
    }
  }
  if (typed.length === 0) return { state: 'none' };
  const added = typed.reduce((sum, f) => addFigures(sum, f, QUANTITY_DECIMALS), '0');
  const total = readFigure(formData?.[SIDE_TOTAL[kind][0]]);
  const show = (f) => Number(f).toLocaleString('en-US', { maximumFractionDigits: QUANTITY_DECIMALS });
  if (total === null) return { state: 'no-total', added: show(added) };
  const left = subtractFigures(total, added, QUANTITY_DECIMALS);
  if (Number(left) === 0) return { state: 'ok', added: show(added), total: show(total) };
  return { state: 'off', added: show(added), total: show(total), left: show(left), over: Number(left) < 0 };
};

export const stopLabel = (kind, index, name) => (
  `${KIND_WORDS[kind].one} ${index + 1}${name ? ` (${name})` : ''}`
);

const portName = (raw) => String(raw || '').split(',')[0].trim();

/**
 * The route in travel order, for the line at the top of the section: the
 * mines, the loading port, the sea, the discharge port, the delivery places.
 * Stops with no partner yet are left out; empty port boxes are left out
 * (inland only, no ship).
 */
export const routeLine = (formData) => {
  const named = (kind) => stopsOf(formData, kind)
    .filter((s) => s.partner_name)
    .map((s) => ({ kind, name: s.partner_name, place: s.address || s.new_address || '' }));
  const loadingPort = portName(formData?.loading_port_location);
  const dischargePort = portName(formData?.discharging_port_location);
  const steps = [...named('loading')];
  if (loadingPort) steps.push({ kind: 'port', name: loadingPort, place: 'Loading port' });
  if (loadingPort || dischargePort) steps.push({ kind: 'sea', name: 'Sea', place: '' });
  if (dischargePort) steps.push({ kind: 'port', name: dischargePort, place: 'Discharge port' });
  steps.push(...named('delivery'));
  return steps.some((s) => s.kind === 'loading' || s.kind === 'delivery') ? steps : [];
};
